import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { requestQuote } from "@/lib/site";
import { Reveal, Overline } from "./Reveal";

const ITEMS = [
  { n: "01", title: "CNC Machining", img: "/media/milling.jpg", span: "md:col-span-2 md:row-span-2",
    text: "Computer-controlled machining of metal and engineering parts to your drawing or sample, from one-offs to batches." },
  { n: "02", title: "CNC Turning & Milling in Wood", service: "CNC Turning & Milling", img: "/media/wood-turning.jpg", span: "",
    text: "Turned and milled wooden pillars, legs, finials and profiles with repeatable accuracy." },
  { n: "03", title: "Iron Cutting", img: "/media/iron.jpg", span: "",
    text: "Iron plates, sections and sheets cut to size and shape for fabrication." },
  { n: "04", title: "Plasma & Laser Cutting", img: "/media/laser.jpg", span: "",
    text: "Clean profiles, gates, grills, signage and decorative metal patterns." },
  { n: "05", title: "CNC Wood Routing", img: "/media/router.jpg", span: "",
    text: "Jaali, carvings, panels and furniture parts routed in teak and boards." },
  { n: "06", title: "Custom Fabrication", img: "/media/welding.jpg", span: "md:col-span-2",
    text: "Gates, frames, structures and one-of-a-kind builds — designed, cut and assembled to order." },
];

const Tile = ({ item, i }) => (
  <motion.article data-testid={`service-card-${item.n}`}
    initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
    className={`group relative overflow-hidden bg-[#0e1116] min-h-[280px] ${item.span}`}>
    <img src={item.img} alt={`${item.title} (illustrative)`} loading="lazy"
      className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-[1200ms] ease-out group-hover:scale-110" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0e1116] via-[#0e1116]/60 to-[#0e1116]/10" />
    <div className="relative h-full flex flex-col justify-between p-6 sm:p-8">
      <div className="flex items-start justify-between">
        <span className="font-mono-tech text-xs tracking-[0.2em] text-[#e8c36a]">{item.n}</span>
        <span className="h-px w-16 bg-gradient-to-r from-[#e8c36a] to-transparent mt-2" />
      </div>
      <div>
        <h3 className={`font-display font-bold text-white tracking-tight ${item.n === "01" ? "text-3xl sm:text-4xl lg:text-5xl" : "text-2xl"}`}>{item.title}</h3>
        <p className="mt-3 text-sm sm:text-base text-slate-200 max-w-md leading-relaxed">{item.text}</p>
        <button data-testid={`service-quote-btn-${item.n}`} onClick={() => requestQuote(item.service || item.title)}
          className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-white border-b border-[#e8c36a]/60 pb-1 hover:text-[#e8c36a] transition-colors">
          Quote this service <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>
      </div>
    </div>
  </motion.article>
);

const CtaTile = () => (
  <Reveal className="md:col-span-2 relative overflow-hidden bg-gold-grad min-h-[280px] p-6 sm:p-8 flex flex-col justify-between">
    <p className="font-mono-tech text-xs tracking-[0.2em] uppercase text-[#2a1f08]">Have a drawing, sketch or photo?</p>
    <div>
      <h3 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0e1116] max-w-md">Send it over. We'll price the cut.</h3>
      <button data-testid="services-cta-quote-btn" onClick={() => requestQuote("")}
        className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#0e1116] text-white font-semibold hover:bg-black transition-colors">
        Start a quotation <ArrowUpRight className="h-4 w-4 text-[#e8c36a]" />
      </button>
    </div>
  </Reveal>
);

export const Services = () => (
  <section id="services" data-testid="services-section" className="py-24 sm:py-32">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-8 mb-14 sm:mb-20">
        <Reveal className="lg:col-span-7">
          <Overline>What we cut, turn & build</Overline>
          <h2 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[0.98]">
            Six disciplines.<br /><span className="text-silver-grad">One standard.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5 lg:pt-12">
          <p className="text-base md:text-lg text-slate-700 leading-relaxed">
            From heavy iron to fine teak, every job is set up, cut and checked in-house. Tell us the material, size and quantity — we'll come back with a clear quotation.
          </p>
          <p className="mt-4 font-mono-tech text-xs text-slate-600">Process imagery on this page is illustrative.</p>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 lg:auto-rows-[300px] gap-4 lg:gap-5">
        {ITEMS.map((it, i) => <Tile key={it.n} item={it} i={i} />)}
        <CtaTile />
      </div>
    </div>
  </section>
);
